export type Domain = 'motor' | 'language' | 'social' | 'cognitive' | 'feeding' | 'selfcare';

export const DOMAIN_META: Record<Domain, { label: string; emoji: string; tw: string; hsl: string }> = {
  motor:     { label: 'Motor',      emoji: '🧗', tw: 'domain-motor',     hsl: 'var(--domain-motor)' },
  language:  { label: 'Language',   emoji: '💬', tw: 'domain-language',  hsl: 'var(--domain-language)' },
  social:    { label: 'Social',     emoji: '🤝', tw: 'domain-social',    hsl: 'var(--domain-social)' },
  cognitive: { label: 'Cognitive',  emoji: '🧩', tw: 'domain-cognitive', hsl: 'var(--domain-cognitive)' },
  feeding:   { label: 'Feeding',    emoji: '🥄', tw: 'domain-feeding',   hsl: 'var(--domain-feeding)' },
  selfcare:  { label: 'Self-care',  emoji: '🪥', tw: 'domain-selfcare',  hsl: 'var(--domain-selfcare)' },
};

export interface Milestone {
  id: string;
  title: string;
  description: string;
  domain: Domain;
  ageMonths: number;       // typical onset
  xp: number;
  science: {
    source: string;        // e.g. "CDC Learn the Signs (2022)"
    quote: string;
    url: string;
  };
}

export interface AgeBand {
  id: string;
  label: string;          // "2 months"
  monthMin: number;
  monthMax: number;
  milestones: Milestone[];
}

export interface Child {
  id: string;
  name: string;
  birthdate: string;       // ISO
  focuses: Domain[];
}

// XP tiers tuned to the current milestone catalog (~2,275 XP reachable).
// Each level is meant to feel like a real step — early levels come quickly,
// later levels reward sustained noticing.
export const TIERS = [
  { min: 0,    name: 'Curious Explorer', quote: 'Every tiny moment is a beginning.' },
  { min: 100,  name: 'Brave Adventurer', quote: 'Courage is small steps, taken often.' },
  { min: 250,  name: 'Bold Explorer',    quote: 'You are noticing what most people miss.' },
  { min: 450,  name: 'Rising Star',      quote: 'Light grows quietly, then all at once.' },
  { min: 700,  name: 'Little Scholar',   quote: 'Curiosity is the truest form of love.' },
  { min: 1000, name: 'Spark of Wonder',  quote: 'Wonder is the foundation of every good life.' },
  { min: 1300, name: 'Thoughtful Soul',  quote: 'Slow attention is the rarest gift.' },
  { min: 1600, name: 'Storyteller',      quote: 'You are writing the story of a lifetime.' },
  { min: 1900, name: 'Quiet Genius',     quote: 'Greatness comes from being deeply seen.' },
  { min: 2200, name: 'Wise One',         quote: 'You have grown alongside them. That is the whole point.' },
];

export function tierFor(xp: number) {
  let current = TIERS[0];
  let next = TIERS[1] ?? TIERS[0];
  for (let i = 0; i < TIERS.length; i++) {
    if (xp >= TIERS[i].min) {
      current = TIERS[i];
      next = TIERS[i + 1] ?? TIERS[i];
    }
  }
  const level = TIERS.findIndex(t => t.name === current.name) + 1;
  const span = (next.min - current.min) || 1;
  const pct = current === next ? 100 : Math.min(100, ((xp - current.min) / span) * 100);
  return { level, name: current.name, quote: current.quote, pct, toNext: Math.max(0, next.min - xp) };
}

export function ageInMonths(birthdate: string) {
  const b = new Date(birthdate);
  const now = new Date();
  return (now.getFullYear() - b.getFullYear()) * 12 + (now.getMonth() - b.getMonth());
}
